import * as React from 'react';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedProps,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { Path, type PathProps } from 'react-native-svg';

const DRAW_DELAY_MS = 180;
const DRAW_RAMP_DURATION_MS = 420;
const DRAW_CRUISE_DURATION_MS = 2300;
const DRAW_FINISH_DURATION_MS = 640;
const DRAW_HOLD_MS = 900;
const RAMP_EASING = Easing.bezier(0.5, 0, 1, 1);
const LINEAR_EASING = Easing.bezier(0, 0, 1, 1);
const FINISH_EASING = Easing.bezier(0.25, 1, 0.5, 1);

const AnimatedPath = Animated.createAnimatedComponent(Path);

type AnimatedRoutePathProps = Readonly<
  Omit<PathProps, 'strokeDasharray' | 'strokeDashoffset'> & {
    enabled?: boolean;
    length: number;
  }
>;

export function AnimatedRoutePath({ enabled = true, length, ...props }: AnimatedRoutePathProps) {
  const reducedMotion = useReducedMotion();
  const shouldAnimate = enabled && !reducedMotion && length > 0;
  const dashOffset = useSharedValue(shouldAnimate ? length : 0);

  React.useEffect(() => {
    cancelAnimation(dashOffset);

    if (!shouldAnimate) {
      dashOffset.set(0);
      return;
    }

    dashOffset.set(length);
    dashOffset.set(
      withDelay(
        DRAW_DELAY_MS,
        withRepeat(
          withSequence(
            withTiming(length * 0.88, {
              duration: DRAW_RAMP_DURATION_MS,
              easing: RAMP_EASING,
            }),
            withTiming(length * 0.08, {
              duration: DRAW_CRUISE_DURATION_MS,
              easing: LINEAR_EASING,
            }),
            withTiming(0, {
              duration: DRAW_FINISH_DURATION_MS,
              easing: FINISH_EASING,
            }),
            withDelay(DRAW_HOLD_MS, withTiming(length, { duration: 0 }))
          ),
          -1,
          false
        )
      )
    );

    return () => {
      cancelAnimation(dashOffset);
    };
  }, [dashOffset, length, shouldAnimate]);

  const animatedProps = useAnimatedProps(() => ({
    strokeDashoffset: dashOffset.value,
  }));

  return (
    <AnimatedPath
      animatedProps={animatedProps}
      strokeDasharray={shouldAnimate ? length : undefined}
      {...props}
    />
  );
}
